import { useState } from 'react';
import {
  getDashboardAgeOverview,
  getDashboardServiceRequests,
  getDashboardStatusOverview,
  getServiceRequestsByProperty,
  getServiceRequestsBySuite,
  getServiceRequestsByContact,
  getServiceRequestsByItem,
} from '../../client';
import { toast } from 'react-toastify';
import {
  ApiResponse,
  IDashboardAgeOverview,
  IDashboardFilters,
  IDashboardStatusOverview,
  IServiceRequest,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';

// Legacy constants for age-based filters.
export const serviceRequestAgeEnum = {
  olderThan60Days: 1,
  between30And60Days: 2,
  daysLessThan30: 3,
};

export interface UseServiceRequestDashboardApiReturn {
  serviceRequests: IServiceRequest[];
  statusOverview: IDashboardStatusOverview;
  ageOverview: IDashboardAgeOverview;
  loading: boolean;
  listLoading: boolean;
  fetchDashboard: (filters?: IDashboardFilters) => Promise<void>;
  fetchByProperty: (propertyId: string) => Promise<void>;
  fetchBySuite: (suiteId: string) => Promise<void>;
  fetchByContact: (contactId: string) => Promise<void>;
  fetchByItem: (itemId: string) => Promise<void>;
  fetchStatusOverview: (filters: IDashboardFilters) => Promise<void>;
  fetchAgeOverview: (filters: IDashboardFilters) => Promise<void>;
  fetchAllOverviews: (filters: IDashboardFilters) => Promise<void>;
}

export const useServiceRequestDashboardApi = (): UseServiceRequestDashboardApiReturn => {
  const [serviceRequests, setServiceRequests] = useState<IServiceRequest[]>([]);
  const [statusOverview, setStatusOverview] =
    useState<IDashboardStatusOverview>({} as IDashboardStatusOverview);
  const [ageOverview, setAgeOverview] = useState<IDashboardAgeOverview>(
    {} as IDashboardAgeOverview
  );

  const [isLoadingList, setIsLoadingList] = useState<boolean>(true);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(true);
  const [isLoadingAge, setIsLoadingAge] = useState<boolean>(true);

  const loading: boolean = isLoadingList || isLoadingStatus || isLoadingAge;

  const fetchDashboard = async (filters?: IDashboardFilters) => {
    try {
      setIsLoadingList(true);
      const res: any = await getDashboardServiceRequests(filters);

      // TODO: apiReq returning { isError: true } on auth fail triggers toast cascades on stale login. Long-term fix: single useAuth provider (blocked on class->FC refactor).
      if (res?.isError) {
        console.warn(
          '[fetchDashboard] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      if (res?.status !== 200) {
        throw new Error('Failed to fetch service requests');
      }

      const resJSON: ApiResponse<IServiceRequest[]> = await res.json();
      const data: IServiceRequest[] = resJSON?.data;
      setServiceRequests(data || []);
    } catch (error) {
      toast.error('Failed to fetch service requests, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchByProperty = async (propertyId: string) => {
    try {
      setIsLoadingList(true);
      const res: any = await getServiceRequestsByProperty(propertyId);

      if (res?.isError) {
        console.warn(
          '[fetchByProperty] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      if (res?.status !== 200) {
        throw new Error('Failed to fetch service requests');
      }

      const resJSON: ApiResponse<IServiceRequest[]> = await res.json();
      const data: IServiceRequest[] = resJSON?.data;
      setServiceRequests(data || []);
    } catch (error) {
      toast.error('Failed to fetch service requests, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchBySuite = async (suiteId: string) => {
    try {
      setIsLoadingList(true);
      const res: any = await getServiceRequestsBySuite(suiteId);

      if (res?.isError) {
        console.warn(
          '[fetchBySuite] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      if (res?.status !== 200) {
        throw new Error('Failed to fetch service requests');
      }

      const resJSON: ApiResponse<IServiceRequest[]> = await res.json();
      const data: IServiceRequest[] = resJSON?.data;
      setServiceRequests(data || []);
    } catch (error) {
      toast.error('Failed to fetch service requests, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchByContact = async (contactId: string) => {
    try {
      setIsLoadingList(true);
      const res: any = await getServiceRequestsByContact(contactId);

      if (res?.isError) {
        console.warn(
          '[fetchByContact] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      if (res?.status !== 200) {
        throw new Error('Failed to fetch service requests');
      }

      const resJSON: ApiResponse<IServiceRequest[]> = await res.json();
      const data: IServiceRequest[] = resJSON?.data;
      setServiceRequests(data || []);
    } catch (error) {
      toast.error('Failed to fetch service requests, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchByItem = async (itemId: string) => {
    try {
      setIsLoadingList(true);
      const res: any = await getServiceRequestsByItem(itemId);

      if (res?.isError) {
        console.warn(
          '[fetchByItem] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      if (res?.status !== 200) {
        throw new Error('Failed to fetch service requests');
      }

      const resJSON: ApiResponse<IServiceRequest[]> = await res.json();
      const data: IServiceRequest[] = resJSON?.data;
      setServiceRequests(data || []);
    } catch (error) {
      toast.error('Failed to fetch service requests, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchStatusOverview = async (filters: IDashboardFilters) => {
    try {
      setIsLoadingStatus(true);
      const res: any = await getDashboardStatusOverview(filters);

      if (res?.isError) {
        console.warn(
          '[fetchStatusOverview] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      const resJSON: ApiResponse<IDashboardStatusOverview> = await res.json();
      const data: IDashboardStatusOverview = resJSON.data;
      setStatusOverview(data);
    } catch (error) {
      toast.error('Failed to fetch status overview, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const fetchAgeOverview = async (filters: IDashboardFilters) => {
    try {
      setIsLoadingAge(true);
      const res: any = await getDashboardAgeOverview(filters);

      if (res?.isError) {
        console.warn(
          '[fetchAgeOverview] apiReq returned isError; suppressing toast (auth lifecycle race — see TODO).',
          res
        );
        return;
      }

      const resJSON: ApiResponse<IDashboardAgeOverview> = await res.json();
      const data: IDashboardAgeOverview = resJSON.data;
      setAgeOverview(data);
    } catch (error) {
      toast.error('Failed to fetch age overview, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setIsLoadingAge(false);
    }
  };

  const fetchAllOverviews = async (filters: IDashboardFilters) => {
    await Promise.all([
      fetchStatusOverview(filters),
      fetchAgeOverview(filters),
    ]);
  };

  return {
    serviceRequests,
    statusOverview,
    ageOverview,
    loading,
    listLoading: isLoadingList,
    fetchDashboard,
    fetchByProperty,
    fetchBySuite,
    fetchByContact,
    fetchByItem,
    fetchStatusOverview,
    fetchAgeOverview,
    fetchAllOverviews,
  };
};
